/**
 * گزارشِ محتوا و کاربر — جدولِ `content_reports`، مایگریشن 0135.
 *
 * دو متد، و هر دو عمداً کوچک‌اند: فهرستِ دلیل‌ها و پاکسازیِ متن در
 * `lib/contentReport.ts` است و این‌جا فقط رفت‌وبرگشتِ دیتابیس.
 *
 * ═══ چرا `add` خطای تکراری را خطا نمی‌داند ═══
 *
 * ایندکس‌های یکتای مایگریشن 0135 می‌گویند «هر کس، هر چیز، یک بار». وقتی
 * کاربر دوباره همان پیام را گزارش می‌دهد، از دیدِ او هیچ اتفاقِ بدی نیفتاده —
 * حرفش قبلاً ثبت شده. پس ۲۳۵۰۵ به `'already'` ترجمه می‌شود و رابط پیامِ
 * درست را می‌گوید، همان قاعده‌ی `issueRepo.add` و `favoriteRepo`.
 *
 * ⚠ این جدول نه ویرایش دارد نه حذف (و دسترسی‌اش هم در دیتابیس پس گرفته
 * شده). پس گرفتنِ گزارش عمداً ممکن نیست — دلیلش بالای همان مایگریشن است.
 */
import { supabase } from '../lib/supabase';
import { isDemoMode } from '../lib/demo';
import {
  REPORT_TARGET_COLUMN, normalizeReportNote,
  type ReportReason, type ReportStatus, type ReportTarget,
} from '../lib/contentReport';
import {
  assertCleanExcept, findObjectionableIn,
  type FilterField, type ObjectionableContentError,
} from '../lib/contentFilter';
import type { UserId } from '../domain/types';

/* eslint-disable @typescript-eslint/no-explicit-any */

/** یک گزارشِ خودم، همان‌طور که رابط لازمش دارد. */
export interface MyReport {
  id: number;
  createdAt: string;
  target: ReportTarget;
  reason: ReportReason;
  note: string | null;
  status: ReportStatus;
}

/** نتیجه‌ی فرستادنِ گزارش — هر دو حالت برای کاربر «موفق»اند. */
export type ReportResult = 'added' | 'already';

export const reportRepo = {
  /**
   * «این را یک نفر باید ببیند.»
   *
   * `uid` صریح گرفته می‌شود و از نشست خوانده نمی‌شود، چون سیاستِ RLS همان را
   * با `auth.uid()` می‌سنجد و اختلافشان باید همان‌جا خطا بدهد، نه بی‌سروصدا
   * ردیفی به اسمِ کسِ دیگری بسازد.
   */
  async add(
    target: ReportTarget, reason: ReportReason, uid: UserId, note?: string | null,
  ): Promise<ReportResult> {
    // دمو نه حسابِ واقعی دارد نه صفی برای رسیدگی؛ دکمه کار می‌کند و چیزی
    // جایی نمی‌رود — همان کاری که بقیه‌ی نوشتن‌های دمو می‌کنند.
    if (isDemoMode) return 'added';

    const { error } = await supabase.from('content_reports').insert({
      reporter_id: uid,
      target_kind: target.kind,
      [REPORT_TARGET_COLUMN[target.kind]]: target.id,
      reason,
      note: normalizeReportNote(note),
    });
    if (error) {
      if (error.code === '23505') return 'already';
      throw error;
    }
    return 'added';
  },

  /**
   * گزارش‌هایی که خودم داده‌ام.
   *
   * فیلترِ `reporter_id` این‌جا لازم نیست: سیاستِ `cr_sel` تضمین می‌کند چیزی
   * جز سطرهای خودِ صداکننده برنگردد — مگر برای ادمین، که او هم برای صف
   * تابعِ خودش را دارد (`opsRepo.contentReports`) و از این مسیر رد نمی‌شود.
   *
   * رابط با این می‌فهمد کدام دکمه باید بگوید «قبلاً گزارش داده‌ای» — بدونش
   * تنها راهِ فهمیدنش خوردن به خطای یکتایی بود، یعنی بعد از فرستادن.
   */
  async mine(): Promise<MyReport[]> {
    if (isDemoMode) return [];
    const { data, error } = await supabase
      .from('content_reports')
      .select('id, created_at, target_kind, signal_id, message_id, profile_id, reason, note, status')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data ?? []).map((r: any): MyReport => ({
      id: Number(r.id),
      createdAt: r.created_at,
      target: r.target_kind === 'message'
        ? { kind: 'message', id: Number(r.message_id) }
        : r.target_kind === 'signal'
          ? { kind: 'signal', id: r.signal_id }
          : { kind: 'profile', id: r.profile_id },
      reason: r.reason,
      note: r.note ?? null,
      status: r.status,
    }));
  },
};

/** آیا این هدف را قبلاً گزارش کرده‌ام — روی فهرستی که `mine()` داده. */
export const alreadyReported = (mine: MyReport[], target: ReportTarget): boolean =>
  mine.some((r) => r.target.kind === target.kind && String(r.target.id) === String(target.id));

/**
 * اعتراض به فیلترِ واژه‌ها (WHOSUP-309، مایگریشن 0160): «این متن را اشتباه
 * گرفت». خودِ خطای فیلتر همه‌چیز را دارد — کدام فیلد، چه متنی، کدام واژه —
 * پس رابط فقط همان را پاس می‌دهد. ردیف خبرِ تلگرام می‌سازد و در ادمین زیرِ
 * «گزارش‌ها» دیده می‌شود.
 */
export async function appealContentFilter(err: ObjectionableContentError): Promise<void> {
  if (isDemoMode) return;
  // uid از نشست: خطا از هر فرمی می‌آید (حتی فرمِ ساختِ نمایه، پیش از آن‌که
  // نمایه‌ای باشد) و سیاستِ `cfa_ins` همان را با `auth.uid()` می‌سنجد.
  const { data: auth } = await supabase.auth.getUser();
  if (!auth.user) throw new Error('not signed in');
  const { error } = await supabase.from('content_filter_appeals').insert({
    user_id: auth.user.id,
    field: err.field,
    body: err.body.slice(0, 2000) || '—',
    matched: err.matched,
  });
  if (error) throw error;
}

/**
 * فیلترِ واژه‌ها برای مسیرهای نوشتن، با اثرِ رأیِ ادمین: واژه‌ای که ادمین
 * اعتراضِ همین کاربر به آن را «اشتباه گرفت» (`status = 'allowed'`) زده،
 * دیگر برای او گیر نمی‌دهد — بی این، کاربر بعد از قبولِ اعتراض هم نمی‌توانست
 * همان متن را بفرستد (تستِ دستیِ `WHOSUP-309-A2`).
 *
 * فقط برای خودِ او، نه همه: یک اعتراضِ قبول‌شده فهرست را برای کسِ دیگری باز
 * نمی‌کند. سیاستِ `cfa_sel` فقط سطرهای خودِ او را نشان می‌دهد. دیتابیس فقط
 * وقتی پرسیده می‌شود که متن واقعاً گیر کرده باشد.
 */
export async function assertCleanForMe(
  field: FilterField, ...texts: (string | null | undefined)[]
): Promise<void> {
  if (findObjectionableIn(...texts).length === 0 || isDemoMode) {
    assertCleanExcept([], field, ...texts);
    return;
  }
  // فیلترِ `user_id` لازم است: برای ادمین `cfa_sel` سطرهای همه را برمی‌گرداند.
  const { data: s } = await supabase.auth.getSession();
  const uid = s.session?.user.id;
  const { data } = uid
    ? await supabase.from('content_filter_appeals').select('matched')
      .eq('status', 'allowed').eq('user_id', uid)
    : { data: [] };
  const exempt = (data ?? []).flatMap((r: any) => (r.matched ?? []) as string[]);
  assertCleanExcept(exempt, field, ...texts);
}
